/**
 * CodeRunnerModal Component
 *
 * Modal for viewing and editing code from chapter content.
 * Code execution is not yet implemented (backend needed).
 */

import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

const CodeRunnerModal = ({ code, language, onClose }) => {
  const [editedCode, setEditedCode] = useState(code);
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const spaces = "    ";
      const next =
        editedCode.substring(0, start) + spaces + editedCode.substring(end);
      setEditedCode(next);
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + spaces.length;
      });
    }
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1e1e1e",
          maxHeight: "85vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #374151",
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="subtitle1" sx={{ color: "white", fontWeight: 600 }}>
            Code Runner
          </Typography>
          <Chip
            label={language}
            size="small"
            sx={{
              bgcolor: "#1e40af",
              color: "white",
              fontFamily: "monospace",
              fontSize: "0.75rem",
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#9ca3af" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <Box
          component="textarea"
          ref={textareaRef}
          value={editedCode}
          onChange={(e) => setEditedCode(e.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          sx={{
            width: "100%",
            minHeight: "200px",
            maxHeight: "360px",
            bgcolor: "#0f0f0f",
            color: "#4ade80",
            fontFamily: '"Fira Code", "Consolas", monospace',
            fontSize: "0.875rem",
            p: 2,
            border: "none",
            resize: "none",
            outline: "none",
            whiteSpace: "pre",
            overflowWrap: "normal",
            overflowX: "auto",
          }}
        />
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #374151",
          px: 2,
          py: 1.5,
          justifyContent: "flex-start",
          gap: 2,
        }}
      >
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          disabled
          sx={{
            bgcolor: "#1e40af",
            opacity: 0.4,
            cursor: "not-allowed",
          }}
        >
          Run
        </Button>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          Execution backend not connected yet
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default CodeRunnerModal;
